import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, SegmentedControl } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';

const W = 600;
const H = 200;

const MODES = [
  { id: 'draw', label: 'Draw' },
  // "Type it" (not "Type") so the i18n key doesn't collide with the noun "Type".
  { id: 'type', label: 'Type it' },
  { id: 'upload', label: 'Upload' },
];

const TYPE_FONTS = [
  { id: 'cursive', label: 'Signature', css: '60px "Brush Script MT", "Segoe Script", cursive' },
  { id: 'serif', label: 'Formal', css: 'italic 52px Georgia, "Times New Roman", serif' },
  { id: 'mono', label: 'Print', css: '46px "Courier New", monospace' },
];

/**
 * SignatureEditor — capture a signature by drawing, typing, or uploading an image.
 * Emits a 600x200 PNG data URL via onSave; onClear removes the stored signature.
 */
export default function SignatureEditor({ initialName = '', onSave, onClear, saving }) {
  const t = useT();
  const [mode, setMode] = useState('draw');
  const [dirty, setDirty] = useState(false);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  // Type mode
  const [typedName, setTypedName] = useState(initialName);
  const [fontId, setFontId] = useState('cursive');

  const ctx = useCallback(() => canvasRef.current?.getContext('2d'), []);

  const clearCanvas = useCallback(() => {
    const c = canvasRef.current;
    const g = ctx();
    if (!c || !g) return;
    g.clearRect(0, 0, W, H);
    setDirty(false);
  }, [ctx]);

  // Render typed text whenever it changes in "type" mode.
  useEffect(() => {
    if (mode !== 'type') return;
    const c = canvasRef.current;
    const g = ctx();
    if (!c || !g) return;
    g.clearRect(0, 0, W, H);
    const text = typedName.trim();
    if (!text) { setDirty(false); return; }
    g.fillStyle = '#111827';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = TYPE_FONTS.find((f) => f.id === fontId)?.css || TYPE_FONTS[0].css;
    g.fillText(text, W / 2, H / 2 + 6);
    setDirty(true);
  }, [mode, typedName, fontId, ctx]);

  // Clear surface when switching to a blank mode.
  useEffect(() => {
    if (mode === 'draw') clearCanvas();
    if (mode === 'upload') clearCanvas();
  }, [mode, clearCanvas]);

  const pointerPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * W,
      y: ((e.clientY - rect.top) / rect.height) * H,
    };
  };

  const startDraw = (e) => {
    if (mode !== 'draw') return;
    drawing.current = true;
    last.current = pointerPos(e);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };
  const moveDraw = (e) => {
    if (!drawing.current || mode !== 'draw') return;
    const g = ctx();
    const p = pointerPos(e);
    g.strokeStyle = '#111827';
    g.lineWidth = 2.4;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.beginPath();
    g.moveTo(last.current.x, last.current.y);
    g.lineTo(p.x, p.y);
    g.stroke();
    last.current = p;
    setDirty(true);
  };
  const endDraw = () => { drawing.current = false; last.current = null; };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const g = ctx();
        g.clearRect(0, 0, W, H);
        const scale = Math.min(W / img.width, H / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        g.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
        setDirty(true);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    const c = canvasRef.current;
    if (!c || !dirty) return;
    onSave?.(c.toDataURL('image/png'));
  };

  const resetSurface = () => {
    clearCanvas();
    if (mode === 'type') setTypedName('');
  };

  return (
    <div className="acct-sig">
      <SegmentedControl options={MODES.map((m) => ({ ...m, label: t(m.label) }))} value={mode} onChange={setMode} ariaLabel={t('Signature method')} />

      {mode === 'type' && (
        <div className="acct-sig__type-row">
          <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder={t('Type your name')} />
          <SegmentedControl
            options={TYPE_FONTS.map((f) => ({ id: f.id, label: t(f.label) }))}
            value={fontId}
            onChange={setFontId}
            ariaLabel={t('Signature style')}
          />
        </div>
      )}

      <div className="acct-sig__surface">
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className={`acct-sig__canvas ${mode === 'draw' ? 'is-draw' : ''}`.trim()}
          onPointerDown={startDraw}
          onPointerMove={moveDraw}
          onPointerUp={endDraw}
          onPointerLeave={endDraw}
        />
        {!dirty && (
          <span className="acct-sig__hint">
            {mode === 'draw' && t('Sign here with your mouse or finger')}
            {mode === 'type' && t('Type your name above')}
            {mode === 'upload' && t('Upload a PNG or JPEG of your signature')}
          </span>
        )}
      </div>

      <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={onUpload} hidden />
      <div className="acct-sig__actions">
        {mode === 'upload' && (
          <Button variant="secondary" size="sm" leadingIcon={<Icon.Upload size={14} />} onClick={() => fileRef.current?.click()} disabled={saving}>
            {t('Choose image')}
          </Button>
        )}
        <span className="acct-sig__spacer" />
        <Button variant="ghost" size="sm" onClick={resetSurface} disabled={saving}>{t('Clear')}</Button>
        {onClear && (
          <Button variant="ghost" size="sm" onClick={onClear} disabled={saving}>{t('Remove saved')}</Button>
        )}
        <Button variant="primary" size="sm" loading={saving} disabled={!dirty} leadingIcon={<Icon.Check size={14} />} onClick={handleSave}>
          {t('Save signature')}
        </Button>
      </div>
    </div>
  );
}
